import { Component } from '@theme/component';
import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Inhoudsopgave van de contentpagina (sections/content-body.liquid). De
 * lijst en de ankers komen van de server; zonder JavaScript springen de
 * links gewoon naar de kop. Dit component voegt toe:
 *
 * - Scroll spy: de actieve kop is de laatste H2 waarvan de bovenkant boven
 *   160px staat; onderaan de pagina de laatste kop die in beeld is.
 * - Zacht scrollen naar de kop (`scroll-margin-top` houdt hem onder de
 *   header), direct bij `prefers-reduced-motion`. De hash in de URL gaat mee
 *   zonder sprong, en de focus naar de kop.
 * - De mobiele uitklapregel klapt in na een klik.
 *
 * Op desktop (vanaf 990px) scrollt Horizon's `.page-wrapper` en niet het
 * venster; scroll-container.js weet welke van de twee het is.
 *
 * @typedef {object} ContentTocRefs
 * @property {HTMLAnchorElement[]} links - De links in beide lijsten (desktop en mobiel).
 * @property {HTMLDetailsElement} [mobile] - De mobiele uitklapregel.
 *
 * @extends Component<ContentTocRefs>
 */
class ContentTocComponent extends Component {
  requiredRefs = ['links'];

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {HTMLElement[]} */
  #headings = [];
  /** @type {EventTarget | null} */
  #scrollTarget = null;
  #frame = 0;
  #activeId = '';

  connectedCallback() {
    super.connectedCallback();
    this.#collectHeadings();
    this.#listen();
    scrollContainerMediaQuery.addEventListener('change', this.#relisten);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unlisten();
    scrollContainerMediaQuery.removeEventListener('change', this.#relisten);
    cancelAnimationFrame(this.#frame);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#activeId = '';
    this.#collectHeadings();
    this.#update();
  }

  /**
   * Een klik op een link in de inhoudsopgave.
   *
   * @param {MouseEvent} event
   */
  scrollToHeading(event) {
    const link = /** @type {Element} */ (event.target).closest('a');
    if (!(link instanceof HTMLAnchorElement)) return;

    const id = idFromLink(link);
    const heading = document.getElementById(id);
    if (!heading) return;

    event.preventDefault();

    // Eerst inklappen: anders schuift de inhoud omhoog terwijl er gescrold wordt.
    if (this.refs.mobile) this.refs.mobile.open = false;

    heading.scrollIntoView({ behavior: this.#reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
    history.replaceState(history.state, '', `#${encodeURIComponent(id)}`);

    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });

    this.#setActive(id);
  }

  #collectHeadings() {
    const ids = new Set(this.refs.links.map(idFromLink));
    this.#headings = [...ids]
      .map((id) => document.getElementById(id))
      .filter((heading) => heading instanceof HTMLElement);
  }

  #listen() {
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#onScroll, { passive: true });
  }

  #unlisten() {
    this.#scrollTarget?.removeEventListener('scroll', this.#onScroll);
    this.#scrollTarget = null;
  }

  #relisten = () => {
    this.#unlisten();
    this.#listen();
    this.#update();
  };

  #onScroll = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const headings = this.#headings;
    const [first] = headings;
    if (!first) return;

    let active = first;
    for (const heading of headings) {
      if (heading.getBoundingClientRect().top < 160) active = heading;
    }

    const container = getScrollContainer();
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 4) {
      const visible = headings.filter((heading) => heading.getBoundingClientRect().top < window.innerHeight);
      active = visible.at(-1) ?? active;
    }

    this.#setActive(active.id);
  }

  /**
   * @param {string} id
   */
  #setActive(id) {
    if (id === this.#activeId) return;
    this.#activeId = id;

    for (const link of this.refs.links) {
      if (idFromLink(link) === id) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }
}

/**
 * @param {HTMLAnchorElement} link
 * @returns {string}
 */
function idFromLink(link) {
  return decodeURIComponent(link.hash.slice(1));
}

if (!customElements.get('content-toc-component')) {
  customElements.define('content-toc-component', ContentTocComponent);
}
